<script setup lang="ts">
import { getCategoryRoute } from '@shopware/helpers'
import type { Schemas } from '#shopware'

const props = defineProps<{
    manufacturers: Schemas['Category'][]
    activeFilter?: string | null
}>()

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)

const FILTER_FIELD_MAP: Record<string, string> = {
    herrenschuhe: 'demographic_men_category_id',
    damenschuhe: 'demographic_women_category_id',
    kinderschuhe: 'demographic_children_category_id',
}

function resolveCategoryPath(category: Schemas['Category']): string {
    const route = getCategoryRoute(category)
    return typeof route === 'string' ? route : route.path
}

function getManufacturerUrl(mfg: Schemas['Category']): string {
    const cf = mfg.customFields as Record<string, unknown> | undefined
    const fieldName = props.activeFilter ? FILTER_FIELD_MAP[props.activeFilter.toLowerCase()] : null
    const categoryId = (fieldName ? cf?.[fieldName] : cf?.demographic_primary_category_id) as string | undefined
    const children = (mfg.children as Schemas['Category'][]) ?? []
    const targetChild = categoryId ? children.find(c => c.id === categoryId) : children[0]
    if (targetChild) {
        return formatLink(resolveCategoryPath(targetChild)) as string
    }
    return formatLink(resolveCategoryPath(mfg)) as string
}

const lexicon = computed<Record<string, Schemas['Category'][]>>(() => {
    const groups: Record<string, Schemas['Category'][]> = {}
    const activeManufacturers = props.manufacturers.filter(mfg => mfg.active)
    for (const mfg of activeManufacturers) {
        const firstChar = mfg.name.slice(0, 1).toUpperCase()
        const key = /^[A-Z]$/.test(firstChar) ? firstChar : '#'
        if (!groups[key]) groups[key] = []
        groups[key].push(mfg)
    }
    for (const key in groups) {
        groups[key]!.sort((a, b) => a.name.localeCompare(b.name))
    }
    return Object.fromEntries(
        Object.entries(groups).sort(([a], [b]) => {
            if (a === '#') return 1
            if (b === '#') return -1
            return a.localeCompare(b)
        })
    )
})

const quickLinksFixed = ref(false)
const sentinelRef = ref<HTMLElement | null>(null)

onMounted(() => {
    const observer = new IntersectionObserver((entries) => {
        quickLinksFixed.value = !entries[0]?.isIntersecting
    })
    if (sentinelRef.value) observer.observe(sentinelRef.value)
    onUnmounted(() => observer.disconnect())
})
</script>

<template>
    <div>
        <div ref="sentinelRef" aria-hidden="true" />

        <div
            :class="[
                'flex flex-col md:hidden z-30 bg-base-100 py-2 px-1 right-0',
                quickLinksFixed
                    ? 'fixed top-1/2 -translate-y-1/2'
                    : 'absolute'
            ]"
        >
            <a
                v-for="char in Object.keys(lexicon)"
                :key="char"
                :href="`#char-${char}`"
                class="px-2 py-1 text-sm"
            >
                {{ char }}
            </a>
        </div>

        <div class="flex flex-wrap">
            <div
                v-for="(mfgs, char) in lexicon"
                :id="`char-${char}`"
                :key="char"
                class="flex gap-3 w-full md:w-1/2 lg:w-1/4 mb-8"
            >
                <div class="w-12 text-6xl mb-3 pb-1">
                    {{ char }}
                </div>
                <div class="flex flex-col gap-y-1">
                    <FoundationLink
                        v-for="mfg in mfgs"
                        :key="mfg.id"
                        :href="getManufacturerUrl(mfg)"
                        class="text-neutral hover:text-primary"
                    >
                        {{ mfg.name }}
                    </FoundationLink>
                </div>
            </div>
        </div>
    </div>
</template>
